import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Skeleton } from "../../Components/Needed";
import { EmptyState, StatusChip } from "../../Components/Kit";
import { archiveLibraryItem, duplicateLibraryItem, fetchLibrary, fetchLibraryMeta, refreshLibrary } from "../../Helpers/notificationConsoleApi";
import TemplateEditor from "../../Components/TemplateEditor";
import MediaTab from "./MediaTab";
import { CHANNEL_NAME, waState } from "./libraryShared";
import { whenText } from "./shared";

// Notification Centre > Library (R6.4): templates as cards (a small picture of each, channel, language, status
// and use), filters, bulk duplicate / archive, and the Media sub-section. A card opens the editor.
const CHANNELS = ["push", "email", "whatsapp", "sms"];
const CHANNEL_TONE = { push: "own", email: "info", whatsapp: "ok", sms: "neutral" };
const WA_TONE = { APPROVED: "ok", FLAGGED: "wait", PENDING: "wait", IN_APPEAL: "wait", REJECTED: "bad", PAUSED: "bad", DISABLED: "bad" };
const NEW_CHOICES = [
    { key: "push", title: "Push", text: "A phone or browser notification, also kept in the in-app inbox." },
    { key: "email", title: "Email", text: "Simple (the email look) or your own HTML." },
    { key: "whatsapp", title: "WhatsApp", text: "Written here and sent to Meta for approval, or linked to an approved one." },
    { key: "sms", title: "SMS", text: "A DLT-registered text with its values." },
];

const statusOf = (t) => {
    if (t.channel === "whatsapp") {
        const s = String(t.whatsapp?.status || "DRAFT").toUpperCase();
        return { tone: WA_TONE[s] || "neutral", text: waState(s).text };
    }
    if (t.channel === "sms") return t.sms?.registeredText ? { tone: "ok", text: "DLT text added" } : { tone: "neutral", text: "No DLT text" };
    return { tone: "ok", text: "Ready" };
};
const usedText = (t) => {
    const owner = t.owner || {};
    if (owner.kind === "event") return `Private · ${owner.eventKey || "one message"}`;
    if (owner.kind === "campaign") return `Private · campaign ${owner.campaignId || ""}`;
    return `Shared · ${t.usedBy ? `used by ${t.usedBy} message${t.usedBy === 1 ? "" : "s"}` : "not used yet"}`;
};

// The card's small picture of the template.
const Thumb = ({ t }) => {
    if (t.channel === "push") {
        return (
            <div className="nc-thumb-push">
                <div className="nc-thumb-app">GetMyDrive · now</div>
                <div className="nc-thumb-title">{t.push?.title || "-"}</div>
                <div className="nc-thumb-text">{t.push?.body || ""}</div>
                {t.thumbUrl ? <img src={t.thumbUrl} alt="" className="nc-thumb-img" loading="lazy" /> : null}
            </div>
        );
    }
    if (t.channel === "email") {
        return (
            <div className="nc-thumb-email">
                <div className="nc-thumb-brand">GetMyDrive</div>
                {t.thumbUrl ? <img src={t.thumbUrl} alt="" className="nc-thumb-img nc-thumb-img-sm" loading="lazy" /> : null}
                <div className="nc-thumb-title">{t.email?.mode === "html" ? t.email?.subject : t.email?.heading || t.email?.subject || "-"}</div>
                <div className="nc-thumb-text">{t.email?.mode === "html" ? "Your own HTML" : t.email?.body || ""}</div>
                {t.email?.buttonLabel ? <span className="nc-thumb-btn">{t.email.buttonLabel}</span> : null}
            </div>
        );
    }
    if (t.channel === "whatsapp") {
        const wa = t.whatsapp || {};
        const linked = wa.source === "meta";
        const firstButton = linked ? "" : wa.type === "carousel" ? (wa.cards?.[0]?.buttons?.[0]?.text || "") : wa.type === "auth" ? "Copy code" : (wa.buttons?.[0]?.type === "COPY_CODE" ? "Copy code" : wa.buttons?.[0]?.text || "");
        return (
            <div className="nc-thumb-wa">
                {t.thumbUrl ? <img src={t.thumbUrl} alt="" className="nc-thumb-img nc-thumb-img-sm" loading="lazy" /> : null}
                <div className="nc-thumb-text">{linked ? `Meta template ${wa.linked?.name || ""}` : wa.type === "auth" ? "123456 is your verification code." : wa.bodyText || ""}</div>
                {firstButton ? <div className="nc-thumb-wa-btn">{firstButton}</div> : null}
                {wa.type === "carousel" ? <div className="nc-thumb-note">{(wa.cards || []).length} cards</div> : null}
            </div>
        );
    }
    return <div className="nc-thumb-sms">{t.sms?.registeredText || t.sms?.providerTemplate || t.sms?.dltTemplateId || "-"}</div>;
};

const Templates = ({ openTab, view, setView, choosing, setChoosing }) => {
    const [meta, setMeta] = useState(null);
    const [items, setItems] = useState(null);
    const [channel, setChannel] = useState("");
    const [language, setLanguage] = useState("");
    const [search, setSearch] = useState("");
    const [archived, setArchived] = useState(false);
    const [showPrivate, setShowPrivate] = useState(false);
    const [picked, setPicked] = useState({});
    const [busy, setBusy] = useState("");

    const load = useCallback(async () => {
        const result = await fetchLibrary({ ...(channel ? { channel } : {}), ...(language ? { language } : {}), ...(archived ? { archived: true } : {}), owner: showPrivate ? "all" : "shared" });
        if (result.ok) setItems(result.data.items || []); else { toast.error(result.message); setItems([]); }
    }, [channel, language, archived, showPrivate]);
    useEffect(() => { (async () => { const r = await fetchLibraryMeta(); if (r.ok) setMeta(r.data); })(); }, []);
    useEffect(() => { if (!view) { setPicked({}); load(); } }, [load, view]);

    const shown = useMemo(() => {
        const term = search.trim().toLowerCase();
        return (items || []).filter((t) => !term || [t.name, t.description, t.whatsapp?.metaName, t.whatsapp?.linked?.name, t.push?.title, t.push?.body, t.email?.subject, t.whatsapp?.bodyText, t.sms?.registeredText].some((v) => String(v || "").toLowerCase().includes(term)));
    }, [items, search]);
    const chosen = Object.keys(picked).filter((id) => picked[id]);

    const bulk = async (kind) => {
        setBusy(kind);
        let done = 0;
        const problems = [];
        for (const id of chosen) {
            // one at a time, so the server's checks (used by a message) apply to each
            // eslint-disable-next-line no-await-in-loop
            const result = kind === "duplicate" ? await duplicateLibraryItem(id) : await archiveLibraryItem(id, !archived);
            if (result.ok) done += 1; else problems.push(`${(items || []).find((t) => t.libraryId === id)?.name || id}: ${result.message}`);
        }
        setBusy("");
        if (done) toast.success(`${kind === "duplicate" ? "Copied" : archived ? "Brought back" : "Archived"}: ${done}`);
        problems.slice(0, 3).forEach((p) => toast.error(p));
        setPicked({});
        load();
    };
    const checkMeta = async () => {
        setBusy("meta");
        const result = await refreshLibrary();
        setBusy("");
        if (!result.ok) return toast.error(result.message);
        toast.success(result.message || "Checked with Meta");
        return load();
    };

    if (view) {
        return (
            <div className="settings-card">
                <TemplateEditor
                    key={view.libraryId || view.channel}
                    target={view.libraryId ? { kind: "library", libraryId: view.libraryId } : { kind: "library", channel: view.channel }}
                    languages={meta?.languages}
                    openTab={openTab}
                    onClose={() => setView(null)}
                />
            </div>
        );
    }

    return (
        <>
            <div className="nc-lib-bar">
                <div className="nc-pills" role="group" aria-label="Channel">
                    {["", ...CHANNELS].map((c) => <button key={c || "all"} type="button" className={`nc-pill ${channel === c ? "is-active" : ""}`} aria-pressed={channel === c} onClick={() => setChannel(c)}>{c ? CHANNEL_NAME[c] : "All"}</button>)}
                </div>
                <input type="search" className="form-control form-control-sm nc-search" placeholder="Search templates" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search templates" />
                {(meta?.languages || []).length > 1 ? (
                    <select className="form-select form-select-sm nc-lib-lang" value={language} onChange={(e) => setLanguage(e.target.value)} aria-label="Language">
                        <option value="">Every language</option>
                        {meta.languages.map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
                    </select>
                ) : null}
                <label className="nc-check"><input type="checkbox" checked={showPrivate} onChange={() => setShowPrivate(!showPrivate)} /> Show private</label>
                <label className="nc-check"><input type="checkbox" checked={archived} onChange={() => setArchived(!archived)} /> Archived</label>
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={checkMeta} disabled={!!busy}>{busy === "meta" ? "Checking..." : "Check WhatsApp status"}</button>
            </div>

            {chosen.length ? (
                <div className="nc-bulk" role="region" aria-label="Selected templates">
                    <span className="fw-bold">{chosen.length} selected</span>
                    <span className="flex-grow-1" />
                    <button type="button" className="nc-bulk-btn" disabled={!!busy} onClick={() => bulk("duplicate")}>{busy === "duplicate" ? "Copying..." : "Duplicate"}</button>
                    <button type="button" className="nc-bulk-btn" disabled={!!busy} onClick={() => bulk("archive")}>{busy === "archive" ? "Working..." : archived ? "Bring back" : "Archive"}</button>
                    <button type="button" className="nc-bulk-clear" disabled={!!busy} onClick={() => setPicked({})}>Clear</button>
                </div>
            ) : null}

            {items === null ? (
                <div className="nc-cards">{[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} height={250} />)}</div>
            ) : shown.length === 0 ? (
                <EmptyState
                    title={(items || []).length ? "No templates match" : archived ? "Nothing archived" : "No templates yet"}
                    text={(items || []).length ? "Change the filters or the search." : archived ? "" : "Write a template once, then use it for any message or campaign."}
                    action={!archived && !(items || []).length ? <button type="button" className="btn btn-primary btn-sm" onClick={() => setChoosing(true)}>New template</button> : null}
                />
            ) : (
                <div className="nc-cards">
                    {shown.map((t) => {
                        const st = statusOf(t);
                        const isPicked = !!picked[t.libraryId];
                        return (
                            <article key={t.libraryId} className={`nc-card ${isPicked ? "is-picked" : ""}`}>
                                <div className={`nc-card-thumb is-${t.channel}`}>
                                    <Thumb t={t} />
                                    <label className="nc-card-pick"><input type="checkbox" checked={isPicked} onChange={() => setPicked((p) => ({ ...p, [t.libraryId]: !p[t.libraryId] }))} aria-label={`Select ${t.name}`} /></label>
                                </div>
                                <div className="nc-card-body">
                                    <div className="nc-card-chips">
                                        <StatusChip tone={CHANNEL_TONE[t.channel]}>{CHANNEL_NAME[t.channel]}</StatusChip>
                                        <StatusChip tone="neutral">{(meta?.languages || []).find((l) => l.code === t.language)?.name || t.language}</StatusChip>
                                        <StatusChip tone={st.tone}>{st.text}</StatusChip>
                                    </div>
                                    <button type="button" className="nc-card-name" onClick={() => setView({ libraryId: t.libraryId })}>{t.name}</button>
                                    <div className="nc-sub">{usedText(t)}{t.updatedAt ? ` · saved ${whenText(t.updatedAt)}` : ""}</div>
                                    {t.whatsapp?.changedSinceSubmit ? <div className="nc-sub text-warning-emphasis">Changes not sent to Meta yet</div> : null}
                                </div>
                            </article>
                        );
                    })}
                </div>
            )}

            {choosing ? (
                <div className="nc-modal-backdrop" role="presentation" onClick={() => setChoosing(false)}>
                    <div className="nc-modal" role="dialog" aria-modal="true" aria-labelledby="nc-new-title" onClick={(e) => e.stopPropagation()}>
                        <div className="d-flex justify-content-between align-items-start gap-2 mb-3">
                            <h2 id="nc-new-title" className="nc-ov-h">New template</h2>
                            <button type="button" className="btn-close" aria-label="Close" onClick={() => setChoosing(false)} />
                        </div>
                        <div className="nc-new-grid">
                            {NEW_CHOICES.map((c) => (
                                <button key={c.key} type="button" className="nc-new-choice" onClick={() => { setChoosing(false); setView({ channel: c.key }); }}>
                                    <span className="fw-bold">{c.title}</span>
                                    <span className="nc-sub">{c.text}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            ) : null}
        </>
    );
};

const LibrarySection = ({ sub, carry, openTab, goSub }) => {
    const tab = sub === "media" ? "media" : "templates";
    const [view, setView] = useState(carry?.libraryId ? { libraryId: carry.libraryId } : null); // { libraryId } | { channel }: the editor
    const [choosing, setChoosing] = useState(false);
    return (
        <div className="nc-lib">
            <div className="nc-sub-row">
                <nav className="nc-subnav" aria-label="Library">
                    <button type="button" className={`nc-subnav-btn ${tab === "templates" ? "is-active" : ""}`} aria-current={tab === "templates" ? "page" : undefined} onClick={() => goSub("templates")}>Templates</button>
                    <button type="button" className={`nc-subnav-btn ${tab === "media" ? "is-active" : ""}`} aria-current={tab === "media" ? "page" : undefined} onClick={() => goSub("media")}>Media</button>
                </nav>
                {tab === "templates" && !view ? <button type="button" className="btn btn-primary btn-sm" onClick={() => setChoosing(true)}>New template</button> : null}
            </div>
            {tab === "media" ? <MediaTab /> : <Templates openTab={openTab} view={view} setView={setView} choosing={choosing} setChoosing={setChoosing} />}
        </div>
    );
};

export default LibrarySection;
